import './videoDisplay.scss';
import { useLocalStorage } from '@custom/dx3';
import Player from '@vimeo/player';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type VideoDisplayProps = {
  videoUrl: string;
  actionName: string;
  actionPerf: (params: any) => void;
};

type VimeoTimeEvent = {
  seconds: number;
  percent: number;
  duration: number;
};

// Wrapper component for the video tag.
// if we need to eventually track that a user has watched the video before
// clicking through, this is where we should do it.
export function VideoDisplay(props: VideoDisplayProps): JSX.Element {
  const [video, setVideo] = useState<HTMLVideoElement | null>(null);
  const vimeoRef = useRef<HTMLIFrameElement | null>(null);
  const [lastUpdated, setLastUpdated] = useState<'currentTime' | 'maxWatched'>(
    'currentTime'
  );

  const [vimeoWidth, setVimeoWidth] = useState<number | undefined>(undefined);

  const [timeTracking, setTimeTracking] = useLocalStorage(props.videoUrl, {
    maxWatched: 0,
    currentTime: 0,
  });

  const performAction = useCallback(() => {
    if (props.videoUrl && props.actionName) {
      props.actionPerf({
        variables: { url: props.videoUrl, action: props.actionName },
      });
    }
  }, [props]);

  const onResize = useCallback(() => {
    const { current } = vimeoRef;
    if (current) {
      setVimeoWidth(current.parentElement?.offsetWidth);
    }
  }, [setVimeoWidth]);

  useLayoutEffect(onResize, [onResize]);
  useEffect(() => {
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, [onResize]);

  const vimeoHeight = useMemo(() => {
    return (vimeoWidth || 0) * 0.588;
  }, [vimeoWidth]);

  // Vimeo prevent skipping ahead
  useEffect(() => {
    const vimeo = vimeoRef.current ? new Player(vimeoRef.current) : null;
    let timeWatched = 0;

    if (!vimeo) {
      return;
    }

    const onTimeUpdate = (e: VimeoTimeEvent) => {
      if (e.seconds - 1 < timeWatched && e.seconds > timeWatched) {
        timeWatched = e.seconds;
      }
    };

    const onSeeked = (e: VimeoTimeEvent) => {
      if (timeWatched < e.seconds) {
        vimeo.setCurrentTime(timeWatched);
      }
    };

    vimeo.on('timeupdate', onTimeUpdate);
    vimeo.on('seeked', onSeeked);
    vimeo.on('ended', performAction);

    return () => {
      vimeo.off('timeupdate', onTimeUpdate);
      vimeo.off('seeked', onSeeked);
      vimeo.off('ended', performAction);
    };
  }, [timeTracking, performAction]);

  useEffect(() => {
    if (!video) {
      return;
    }

    const timeUpdate = function () {
      //Don't do anything if this is a seek, preventSeeking function handles that portion
      if (!video.seeking) {
        if (video.currentTime >= timeTracking.maxWatched) {
          // record that the user has made progress through the video
          setLastUpdated('maxWatched');
          timeTracking.maxWatched = video.currentTime;
        } else {
          // record where the user is in his current watch session
          setLastUpdated('currentTime');
          timeTracking.currentTime = video.currentTime;
        }
      }
      if (!document.hasFocus()) {
        video.pause();
      }
      // NOTE: this does NOT update when you think it does. It doesn't save and then update the timeTracking variable. This is ONLY for reloads.
      setTimeTracking(timeTracking);
    };

    const preventSeeking = function () {
      const delta = video.currentTime - timeTracking.maxWatched;
      if (delta > 0.01) {
        video.pause();
        //play back from where the user started seeking after rewind or without rewind
        video.currentTime = timeTracking[lastUpdated];
        video.play();
      }
    };

    const onEnd = function () {
      performAction();
    };

    video.addEventListener('timeupdate', timeUpdate);
    video.addEventListener('seeking', preventSeeking);
    video.addEventListener('ended', onEnd);

    return () => {
      video.removeEventListener('timeupdate', timeUpdate);
      video.removeEventListener('seeking', preventSeeking);
      video.removeEventListener('ended', onEnd);
    };
  }, [
    video,
    timeTracking,
    lastUpdated,
    setLastUpdated,
    setTimeTracking,
    performAction,
  ]);

  let validUrl = true;
  let url;

  try {
    url = new URL(props.videoUrl);
  } catch {
    console.error(
      props.videoUrl,
      'is not a valid URL. Please contact your system administrator'
    );
    validUrl = false;
  }

  return validUrl ? (
    url?.origin.includes('vimeo') ? (
      <iframe
        ref={vimeoRef}
        src={props.videoUrl}
        allow="autoplay; fullscreen"
        className="video-display-element"
        width={vimeoWidth}
        height={vimeoHeight}
      />
    ) : (
      <video ref={setVideo} controls autoPlay className="video-display-element">
        <source src={props.videoUrl} />
      </video>
    )
  ) : (
    <></>
  );
}
